// Dashboard — the Books home page. Cash and income at a glance, a twelve-month
// income and expense chart, what needs a hand, recent activity, and each
// member's capital. Reads only; every number links to the page behind it.
import { useEffect, useMemo, useState } from 'react';
import { ErrorNote } from '../../components/ui';
import { useBooksAccess } from '../../contexts/AuthContext';
import { dateTime, dollars, monthKey, monthName, pct } from '../../lib/format';
import { access, accounts as accountsApi, journal, reports, settings } from '../../lib/ledger/api';
import { MEMBERS, SOURCE_LABELS, type Account, type JournalEntry, type LedgerLine, type Member, type MemberSettings, type Overview } from '../../lib/ledger/types';
import { BOOKS_ICONS, type BooksTab } from './nav';
import { localIso, Money } from './shared';

const DEFAULT_NAMES: MemberSettings = { leah: { name: 'Leah' }, laura: { name: 'Laura Means' } };
const DEFAULT_SHARES: Record<Member, number> = { leah: 51, laura: 49 };
const INCOME = 'var(--bk-income)';
const EXPENSE = 'var(--bk-expense)';

type MonthBucket = { key: string; income: number; expense: number };
type Attention = { key: string; tone: 'warn' | 'info'; title: string; line: string; tab: BooksTab; label: string };

function firstOfMonthsAgo(n: number, now = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth() - n, 1);
}
const shortMonth = (key: string) => new Date(`${key}-01T12:00:00`).toLocaleDateString('en-US', { month: 'short' });

/** Round a maximum up to a tidy axis top (1, 2, 2.5, 5 × 10ⁿ). */
function niceTop(v: number): number {
  if (v <= 0) return 10000;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}
function axisMoney(cents: number): string {
  const d = cents / 100;
  if (Math.abs(d) >= 1000) return `$${(d / 1000).toFixed(d % 1000 === 0 ? 0 : 1)}k`;
  return `$${d.toFixed(0)}`;
}

export function OverviewTab({ go }: { go: (t: BooksTab) => void }) {
  const { canEdit } = useBooksAccess();
  const [ov, setOv] = useState<Overview | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [names, setNames] = useState<MemberSettings>(DEFAULT_NAMES);
  const [shares, setShares] = useState<Record<Member, number>>(DEFAULT_SHARES);
  const [accountants, setAccountants] = useState<number | null>(null);
  const [lines, setLines] = useState<LedgerLine[]>([]);
  const [chart, setChart] = useState<Account[]>([]);
  const [recent, setRecent] = useState<JournalEntry[]>([]);

  const now = new Date();
  const windowStart = localIso(firstOfMonthsAgo(11, now));
  const todayIso = localIso(now);
  const yearKey = String(now.getFullYear());

  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      reports.overview(), settings.all(), settings.shares(), access.list(),
      reports.ledger(windowStart, todayIso), accountsApi.list(),
      journal.page({ from: '1900-01-01', to: todayIso, limit: 50 }),
    ]).then(([o, s, sh, a, gl, ac, jp]) => {
      if (!alive) return;
      if (o.status === 'fulfilled' && o.value) setOv(o.value); else setErr(o.status === 'rejected' ? o.reason : new Error('The dashboard came back empty.'));
      if (s.status === 'fulfilled') { const m = s.value.members as MemberSettings | undefined; if (m?.leah?.name && m?.laura?.name) setNames(m); }
      if (sh.status === 'fulfilled' && sh.value) setShares(sh.value);
      if (a.status === 'fulfilled' && Array.isArray(a.value)) setAccountants(a.value.filter((r) => !r.is_admin && r.level != null).length);
      if (gl.status === 'fulfilled') setLines(gl.value ?? []);
      if (ac.status === 'fulfilled') setChart(ac.value ?? []);
      if (jp.status === 'fulfilled') {
        const sorted = [...(jp.value?.entries ?? [])].sort((x, y) => (y.entry_date + y.created_at).localeCompare(x.entry_date + x.created_at));
        setRecent(sorted.slice(0, 7));
      }
      setLoading(false);
    });
    return () => { alive = false; };
  }, [windowStart, todayIso]);

  // Twelve months of income and expense from the general ledger.
  const months: MonthBucket[] = useMemo(() => {
    const typeOf = new Map(chart.map((a) => [a.id, a.type]));
    const buckets = new Map<string, MonthBucket>();
    for (let i = 11; i >= 0; i--) { const k = monthKey(firstOfMonthsAgo(i, now)); buckets.set(k, { key: k, income: 0, expense: 0 }); }
    for (const l of lines) {
      const b = buckets.get(l.entry_date.slice(0, 7)); if (!b) continue;
      const t = typeOf.get(l.account_id);
      if (t === 'income') b.income += Number(l.credit_cents) - Number(l.debit_cents);
      else if (t === 'expense' || t === 'cogs') b.expense += Number(l.debit_cents) - Number(l.credit_cents);
    }
    return [...buckets.values()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, chart]);
  const ytd = months.filter((m) => m.key.startsWith(yearKey)).reduce((s, m) => ({ income: s.income + m.income, expense: s.expense + m.expense }), { income: 0, expense: 0 });

  if (loading) return <div className="bk-grid">{[0, 1, 2, 3].map((i) => <div key={i} className="bk-card bk-span-3 bk-stat"><div className="bk-stat-label">Loading…</div><div className="bk-stat-num">&nbsp;</div></div>)}</div>;
  if (err || !ov) return <ErrorNote error={err ?? new Error('The dashboard is not available right now.')} notConfigured="The ledger is not set up yet." />;

  const attention: Attention[] = [];
  if (ov.for_review > 0) attention.push({ key: 'bank', tone: 'warn', title: `${ov.for_review} bank ${ov.for_review === 1 ? 'item' : 'items'} to review`, line: 'Imported and not yet matched to an account.', tab: 'banking', label: 'Review' });
  if (ov.apple.month && !ov.apple.closed) attention.push({ key: 'apple', tone: 'warn', title: `Close Apple month ${monthName(ov.apple.month)}`, line: 'Closing locks the split and posts it to the ledger.', tab: 'apple', label: 'Open' });
  const lastMonth = monthKey(firstOfMonthsAgo(1, now));
  const stale = (ov.open_months ?? []).filter((m) => m < lastMonth);
  if (stale.length) attention.push({ key: 'periods', tone: 'info', title: `${stale.length} older ${stale.length === 1 ? 'month is' : 'months are'} still open`, line: `Oldest: ${monthName(`${stale[0]}-01`)}.`, tab: 'periods', label: 'Month end' });
  if (accountants === 0) attention.push({ key: 'access', tone: 'info', title: 'No accountant has access yet', line: 'Invite them so they can work in the books directly.', tab: 'access', label: 'Invite' });

  const curKey = monthKey();
  const stats: { label: string; cents: number; sub: string; tab: BooksTab }[] = [
    { label: 'Cash', cents: ov.vessel_cents, sub: 'Rootegra’s operating cash', tab: 'banking' },
    { label: 'Due from Apple', cents: ov.due_from_apple_cents, sub: ov.apple.month ? `${monthName(ov.apple.month)} · ${ov.apple.paid ? 'paid' : 'not paid yet'}` : 'No Apple month yet', tab: 'apple' },
    { label: `Net income ${yearKey}`, cents: ov.net_income_ytd_cents, sub: `Through ${monthName(`${curKey}-01`)}`, tab: 'reports' },
    { label: 'Net income this month', cents: ov.net_income_month_cents, sub: monthName(`${curKey}-01`), tab: 'reports' },
  ];

  return (
    <div className="bk-grid">
      {stats.map((s) => (
        <button key={s.label} type="button" className="bk-card bk-span-3 bk-stat" style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit' }} onClick={() => go(s.tab)}>
          <div className="bk-stat-label">{s.label}</div>
          <div className={`bk-stat-num${s.cents < 0 ? ' neg' : ''}`}>{s.cents < 0 ? `(${dollars(-s.cents)})` : dollars(s.cents)}</div>
          <div className="bk-stat-sub">{s.sub}</div>
        </button>
      ))}

      <section className="bk-card bk-span-4">
        <div className="bk-card-head"><h2 className="bk-card-title">Profit and loss</h2><span className="bk-card-sub">{yearKey} to date</span></div>
        <div className="bk-card-body">
          <PlBar label="Income" cents={ytd.income} max={Math.max(ytd.income, ytd.expense, 1)} color={INCOME} />
          <PlBar label="Expenses" cents={ytd.expense} max={Math.max(ytd.income, ytd.expense, 1)} color={EXPENSE} />
          <div className="bk-pl-net"><span>Net income</span><Money cents={ytd.income - ytd.expense} /></div>
        </div>
        <div className="bk-card-foot"><button type="button" className="bk-link" onClick={() => go('reports')}>Open the full profit and loss →</button></div>
      </section>

      <section className="bk-card bk-span-8">
        <div className="bk-card-head">
          <h2 className="bk-card-title">Income and expenses, last 12 months</h2>
          <div className="bk-legend"><span><i style={{ background: INCOME }} />Income</span><span><i style={{ background: EXPENSE }} />Expenses</span></div>
        </div>
        <div className="bk-card-body"><MonthChart months={months} /></div>
      </section>

      <section className="bk-card bk-span-4">
        <div className="bk-card-head"><h2 className="bk-card-title">Needs attention</h2><span className="bk-card-sub">{attention.length || 'All clear'}</span></div>
        <div className="bk-card-body">
          {attention.length === 0 ? (
            <ul className="bk-todo"><li><span className="bk-todo-ico bk-todo-ico--ok">✓</span><div><div className="bk-todo-title">Nothing waiting</div><div className="bk-todo-line">Bank, Apple, and month end are up to date.</div></div><span /></li></ul>
          ) : (
            <ul className="bk-todo">
              {attention.map((a) => (
                <li key={a.key}>
                  <span className={`bk-todo-ico bk-todo-ico--${a.tone}`}>{a.tone === 'warn' ? '!' : 'i'}</span>
                  <div><div className="bk-todo-title">{a.title}</div><div className="bk-todo-line">{a.line}</div></div>
                  <button type="button" className="bk-link" onClick={() => go(a.tab)}>{a.label} →</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="bk-card bk-span-5">
        <div className="bk-card-head"><h2 className="bk-card-title">Recent activity</h2><span className="bk-card-sub">{ov.last_entry_at ? `Last entry ${dateTime(ov.last_entry_at)}` : 'No entries yet'}</span></div>
        <div className="bk-card-body">
          {recent.length === 0 ? (
            <p className="bk-todo-line" style={{ margin: 0 }}>Entries show here as months close and bills are recorded.</p>
          ) : (
            <table className="bk-mini"><tbody>
              {recent.map((e) => {
                const total = e.lines.reduce((s, l) => s + Number(l.debit_cents), 0);
                return (
                  <tr key={e.id}>
                    <td>{e.memo || SOURCE_LABELS[e.source]}<span className="sub">{new Date(`${e.entry_date}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · {SOURCE_LABELS[e.source]}</span></td>
                    <td className="num"><Money cents={total} /></td>
                  </tr>
                );
              })}
            </tbody></table>
          )}
        </div>
        <div className="bk-card-foot"><button type="button" className="bk-link" onClick={() => go('journal')}>View all entries →</button></div>
      </section>

      <section className="bk-card bk-span-3">
        <div className="bk-card-head"><h2 className="bk-card-title">Member capital</h2></div>
        <div className="bk-card-body">
          <table className="bk-mini"><tbody>
            {MEMBERS.map((m) => (
              <tr key={m}><td>{names[m]?.name ?? m}<span className="sub">{pct(shares[m])} share</span></td><td className="num"><Money cents={ov.members?.[m]?.capital_cents ?? 0} /></td></tr>
            ))}
          </tbody></table>
        </div>
        <div className="bk-card-foot"><button type="button" className="bk-link" onClick={() => go('members')}>Capital accounts →</button></div>
      </section>

      <section className="bk-card bk-span-12">
        <div className="bk-card-head"><h2 className="bk-card-title">{canEdit ? 'Quick actions' : 'Go to'}</h2></div>
        <div className="bk-card-body">
          <div className="bk-quick">
            {(canEdit
              ? ([['spending', 'Record an expense'], ['income', 'Record income'], ['journal', 'Journal entry'], ['banking', 'Import a statement'], ['apple', 'Close an Apple month'], ['members', 'Contribution or distribution'], ['assets', 'Add a fixed asset'], ['reports', 'Run reports']] as [BooksTab, string][])
              : ([['reports', 'Reports'], ['journal', 'Journal'], ['banking', 'Banking'], ['members', 'Members'], ['access', 'Access and log']] as [BooksTab, string][])
            ).map(([t, label]) => (
              <button key={label} type="button" onClick={() => go(t)}>{BOOKS_ICONS[t]}{label}</button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function PlBar({ label, cents, max, color }: { label: string; cents: number; max: number; color: string }) {
  const w = Math.max(0, Math.min(100, (Math.max(cents, 0) / max) * 100));
  return (
    <div className="bk-pl-row">
      <span>{label}</span>
      <span className="bk-pl-bar"><i style={{ width: `${w}%`, background: color }} /></span>
      <Money cents={cents} />
    </div>
  );
}

function MonthChart({ months }: { months: MonthBucket[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);
  const W = 720, H = 230, L = 52, R = 8, T = 10, B = 26;
  const plotW = W - L - R, plotH = H - T - B;
  const top = niceTop(Math.max(...months.flatMap((m) => [m.income, m.expense]), 0));
  const y = (v: number) => T + plotH - (Math.max(v, 0) / top) * plotH;
  const col = plotW / months.length;
  const bw = Math.min(16, (col - 10) / 2);
  const empty = months.every((m) => m.income === 0 && m.expense === 0);
  const bar = (x: number, v: number, fill: string) => {
    const h = Math.max(0, T + plotH - y(v));
    if (h <= 0) return null;
    const r = Math.min(4, h, bw / 2);
    const yt = T + plotH - h;
    return <path d={`M${x},${T + plotH} V${yt + r} Q${x},${yt} ${x + r},${yt} H${x + bw - r} Q${x + bw},${yt} ${x + bw},${yt + r} V${T + plotH} Z`} fill={fill} />;
  };

  if (asTable) {
    return (
      <>
        <table className="bk-mini"><tbody>
          <tr><td><b>Month</b></td><td className="num"><b>Income</b></td><td className="num"><b>Expenses</b></td><td className="num"><b>Net</b></td></tr>
          {months.map((m) => <tr key={m.key}><td>{monthName(`${m.key}-01`)}</td><td className="num"><Money cents={m.income} /></td><td className="num"><Money cents={m.expense} /></td><td className="num"><Money cents={m.income - m.expense} /></td></tr>)}
        </tbody></table>
        <button type="button" className="bk-link" style={{ marginTop: 8 }} onClick={() => setAsTable(false)}>Show as chart</button>
      </>
    );
  }

  const h = hover != null ? months[hover] : null;
  return (
    <div className="bk-chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Income and expenses by month for the last twelve months">
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <g key={f}>
            <line x1={L} x2={W - R} y1={y(top * f)} y2={y(top * f)} stroke={f === 0 ? 'var(--bk-line-strong)' : '#EFECE6'} />
            <text x={L - 8} y={y(top * f) + 4} textAnchor="end" fontSize="11" fill="var(--bk-ink-3)">{axisMoney(top * f)}</text>
          </g>
        ))}
        {months.map((m, i) => {
          const x0 = L + i * col + (col - (bw * 2 + 2)) / 2;
          return (
            <g key={m.key}>
              {hover === i && <rect x={L + i * col + 2} y={T} width={col - 4} height={plotH} fill="#F4F1EB" rx="4" />}
              {bar(x0, m.income, INCOME)}
              {bar(x0 + bw + 2, m.expense, EXPENSE)}
              <text x={L + i * col + col / 2} y={H - 8} textAnchor="middle" fontSize="11" fill="var(--bk-ink-3)">{shortMonth(m.key)}</text>
              <rect x={L + i * col} y={T} width={col} height={plotH + B} fill="transparent" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
            </g>
          );
        })}
        {empty && <text x={L + plotW / 2} y={T + plotH / 2} textAnchor="middle" fontSize="13" fill="var(--bk-ink-3)">No income or expenses posted yet</text>}
      </svg>
      {h && hover != null && (
        <div className="bk-tip" style={{ left: `${((L + hover * col + col / 2) / W) * 100}%`, top: `${(y(Math.max(h.income, h.expense)) / H) * 100}%` }}>
          <b>{monthName(`${h.key}-01`)}</b>
          <div><span>Income</span><span>{dollars(h.income)}</span></div>
          <div><span>Expenses</span><span>{dollars(h.expense)}</span></div>
          <div><span>Net</span><span>{dollars(h.income - h.expense)}</span></div>
        </div>
      )}
      <button type="button" className="bk-link" style={{ marginTop: 6 }} onClick={() => setAsTable(true)}>Show as table</button>
    </div>
  );
}
